import { cloneElement, useCallback, useEffect, useState } from 'react';
import type { ReactElement, Ref } from 'react';

import { useCategoryDropdownContext } from './CategoryDropdownContext';

export interface CategoryDropdownTriggerProps {
  /**
   * 단일 ReactElement — onClick / aria-expanded / aria-haspopup / ref 가 자동 주입됨.
   *
   * Click 은 CategoryDropdown 의 open state 를 toggle.
   * mount 시 자체 element 가 CategoryDropdown 의 anchor 로 자동 등록 (단일 trigger 케이스에서 anchorEl 명시 불필요).
   *
   * children 이 이미 ref 를 가지고 있으면 그쪽도 forward 됨 (multi-ref merge).
   */
  children: ReactElement;
}

/**
 * Generic trigger — 호스트의 임의 element 를 CategoryDropdown trigger 로 enhance.
 *
 * PopOver.Trigger 와 동일 패턴 (cloneElement, asChild prop 없이 children 항상 enhance).
 *
 * 호스트 사용:
 * ```tsx
 * <CategoryDropdown.Trigger>
 *   <button>카테고리</button>
 * </CategoryDropdown.Trigger>
 * ```
 *
 * Top icon button 의 흔한 케이스는 `Header.CategoryTrigger` preset 사용 권장.
 */
export function CategoryDropdownTrigger({ children }: CategoryDropdownTriggerProps) {
  const ctx = useCategoryDropdownContext();

  if (process.env.NODE_ENV !== 'production' && ctx === null) {
    // eslint-disable-next-line no-console
    console.warn('[core-ui] CategoryDropdown.Trigger requires <CategoryDropdown> ancestor. Click will no-op.');
  }

  // ── 자동 등록 — 책임 분리 ─────────────────────────────────────
  // [책임 1] callback ref → element 를 state 로 저장 + child 의 기존 ref forward.
  // [책임 2] useEffect → state 변경 시 register / cleanup 시 unregister.

  // child 가 이미 보유한 ref (e.g. <button ref={hostRef}>) 도 forward 대상.
  const childRef = (children as ReactElement & { ref?: Ref<HTMLElement> }).ref;

  // [책임 1] element 를 state 로 저장 + child ref forward
  const [el, setEl] = useState<HTMLElement | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      setEl(node);
      // forward to child's existing ref (object ref 또는 callback ref 둘 다 지원)
      if (typeof childRef === 'function') {
        childRef(node);
      } else if (childRef) {
        (childRef as { current: HTMLElement | null }).current = node;
      }
    },
    [childRef],
  );

  // [책임 2] element/ctx 변경 시 register, cleanup 시 unregister
  useEffect(() => {
    if (!el || !ctx) return;
    return ctx.registerTrigger(el);
  }, [el, ctx]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const childOnClick = (children.props as { onClick?: (e: React.MouseEvent<HTMLElement>) => void }).onClick;
    childOnClick?.(e);
    if (!e.defaultPrevented && ctx !== null) {
      ctx.handleTriggerClick();
    }
  };

  return cloneElement(children, {
    onClick: handleClick,
    ref,
    'aria-expanded': ctx?.open ?? false,
    'aria-haspopup': 'menu' as const,
  } as Partial<typeof children.props> & { ref: Ref<HTMLElement> });
}

CategoryDropdownTrigger.displayName = 'CategoryDropdown.Trigger';
